"use client"

import { useState, useEffect, useCallback } from "react"

interface TextScrambleProps {
  text: string
  className?: string
  scrambleOnHover?: boolean
  delay?: number
  speed?: number
  randomInterval?: number // If set, scramble randomly at this average interval (ms)
}

// Random letters, numbers & special characters for scrambling effect
const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%&*<>[]{}/"

export function TextScramble({
  text,
  className = "",
  scrambleOnHover = false,
  delay = 0,
  speed = 50,
  randomInterval
}: TextScrambleProps) {
  const [displayText, setDisplayText] = useState(text)
  const [isScrambling, setIsScrambling] = useState(false)

  const scramble = useCallback(() => {
    if (isScrambling) return
    setIsScrambling(true)

    let iteration = 0
    const maxIterations = text.length * 3

    const interval = setInterval(() => {
      setDisplayText(
        text
          .split("")
          .map((char, index) => {
            if (char === " ") return " "
            if (index < iteration / 3) {
              return text[index]
            }
            return chars[Math.floor(Math.random() * chars.length)]
          })
          .join("")
      )

      iteration++

      if (iteration >= maxIterations) {
        clearInterval(interval)
        setDisplayText(text)
        setIsScrambling(false)
      }
    }, speed)

    return () => clearInterval(interval)
  }, [text, isScrambling, speed])

  // Initial scramble on mount with delay
  useEffect(() => {
    const timeout = setTimeout(() => {
      scramble()
    }, delay)
    return () => clearTimeout(timeout)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Random interval scrambling
  useEffect(() => {
    if (!randomInterval) return

    const scheduleNext = () => {
      // Random time between 0.5x and 1.5x the interval
      const nextTime = randomInterval * (0.5 + Math.random())
      return setTimeout(() => {
        scramble()
        scheduleNext()
      }, nextTime)
    }

    const timeout = scheduleNext()
    return () => clearTimeout(timeout)
  }, [randomInterval, scramble])

  return (
    <span
      className={className}
      onMouseEnter={scrambleOnHover ? scramble : undefined}
    >
      {displayText}
    </span>
  )
}
